import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface FilterOption {
  value: string;
  label: string;
  count?: number;
  /** Dot + active-ring colour. Falls back to the neutral border colour. */
  color?: string;
}

interface SalesFilterDropdownProps {
  label: string;
  options: FilterOption[];
  selected: string[];
  onToggle: (value: string) => void;
  onClear?: () => void;
  /** Hide the per-option count badge (flags have no meaningful per-option count). */
  showCounts?: boolean;
  title?: string;
  /** `chip` sits in a chip row (e.g. Targets beside the tiers) and shows `count` like a tier chip. */
  variant?: 'button' | 'chip';
  count?: number;
  color?: string;
}

/**
 * Multi-select filter dropdown, matching the Broker View `FilterDropdown`
 * pattern: a compact trigger carrying an active-count badge, and a portalled
 * panel of toggle chips.
 *
 * The panel is PORTALLED to <body> and positioned fixed. The control row wraps
 * and scrolls, so an in-flow panel would be clipped by the ancestor's overflow.
 */
export function SalesFilterDropdown({
  label,
  options,
  selected,
  onToggle,
  onClear,
  showCounts = true,
  title,
  variant = 'button',
  count,
  color,
}: SalesFilterDropdownProps) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const close = useCallback(() => setOpen(false), []);

  const place = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const a = anchor.getBoundingClientRect();
    const w = popRef.current?.offsetWidth ?? 240;
    // Clamp so a control near the right edge does not open off-screen.
    const left = Math.max(8, Math.min(a.left, window.innerWidth - w - 8));
    setPos({ top: a.bottom + 4, left });
  }, []);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (anchorRef.current && t && anchorRef.current.contains(t)) return;
      if (t?.closest?.('.sv-filterdd-pop')) return;
      close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, close, place]);

  const activeCount = selected.length;

  return (
    <div className="sv-filterdd" ref={anchorRef}>
      {variant === 'chip' ? (
        <button
          type="button"
          className={`sv-chip sv-chip-dd ${activeCount > 0 ? 'active' : ''}`}
          style={color ? { color } : undefined}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-haspopup="listbox"
          title={title}
        >
          <span className="sv-chip-dot" />
          <span className="sv-chip-label">{label}{activeCount > 0 ? ` (${activeCount})` : ''}</span>
          {count != null && <span className="sv-chip-count">{count.toLocaleString()}</span>}
          <span className="sv-filterdd-caret">▾</span>
        </button>
      ) : (
      <button
        type="button"
        className={`sv-filterdd-btn ${activeCount > 0 ? 'active' : ''} ${open ? 'open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title={title}
      >
        <span className="sv-filterdd-label">{label}</span>
        {activeCount > 0 && <span className="sv-filterdd-badge">{activeCount}</span>}
        <span className="sv-filterdd-caret">▾</span>
      </button>
      )}

      {open &&
        createPortal(
          <div
            className="sv-filterdd-pop"
            ref={popRef}
            style={{ top: pos.top, left: pos.left }}
          >
            <div className="sv-filterdd-pop-head">
              <span>{label}</span>
              {onClear && activeCount > 0 && (
                <button type="button" className="sv-filterdd-clear" onClick={onClear}>
                  Clear
                </button>
              )}
            </div>
            <div className="sv-filterdd-chips">
              {options.length === 0 && <span className="sv-filterdd-empty">No options</span>}
              {options.map((o) => {
                const isActive = selected.includes(o.value);
                return (
                  <button
                    key={o.value}
                    type="button"
                    className={`sv-chip ${isActive ? 'active' : ''}`}
                    style={o.color ? { color: o.color } : undefined}
                    aria-pressed={isActive}
                    onClick={() => onToggle(o.value)}
                  >
                    {o.color && <span className="sv-chip-dot" />}
                    <span className="sv-chip-label">{o.label}</span>
                    {showCounts && o.count != null && (
                      <span className="sv-chip-count">{o.count.toLocaleString()}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

export default SalesFilterDropdown;
